import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Download, RefreshCw, Cloud, CloudOff, Clock, CheckCircle, Loader2, Link2, Search, AlertCircle, CheckCircle2, XCircle, Trash2, Calendar } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function AdminTransactions() {
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [debugPhone, setDebugPhone] = useState('');
  const [debugEmail, setDebugEmail] = useState('');
  const [debugOrderNumber, setDebugOrderNumber] = useState('');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const [shopifyOrderResult, setShopifyOrderResult] = useState(null);
  const [checkingShopify, setCheckingShopify] = useState(false);
  
  // Manual Match state
  const [unmatchedTransactions, setUnmatchedTransactions] = useState([]);
  const [manualMatchDialogOpen, setManualMatchDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [referralSearch, setReferralSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [matching, setMatching] = useState(false);
  const [unmatchedSearch, setUnmatchedSearch] = useState('');
  const [filteredUnmatched, setFilteredUnmatched] = useState([]);

  // Attribute-to-Partner state
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [attributeTxn, setAttributeTxn] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [allPartners, setAllPartners] = useState([]);
  const [attributing, setAttributing] = useState(false);

  // Reconcile state
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileStart, setReconcileStart] = useState('');
  const [reconcileEnd, setReconcileEnd] = useState('');
  const [reconcileResult, setReconcileResult] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  // Custom date-range sync state
  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  useEffect(() => {
    fetchTransactions();
    fetchSyncStatus();
    fetchUnmatchedTransactions();
    
    // Handle OAuth callback messages
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'connected') {
      toast.success('Shopify connected successfully!');
      // Clear URL params
      window.history.replaceState({}, '', '/admin/transactions');
    } else if (error) {
      const errorMessages = {
        'oauth_denied': 'Shopify authorization was denied',
        'missing_params': 'Missing OAuth parameters',
        'invalid_state': 'Invalid OAuth state - please try again',
        'token_exchange_failed': 'Failed to get access token from Shopify',
        'no_token': 'No access token received',
        'callback_failed': 'OAuth callback failed'
      };
      toast.error(errorMessages[error] || 'Shopify connection failed');
      window.history.replaceState({}, '', '/admin/transactions');
    }
  }, [searchParams]);

  // Auto-poll sync status if a background sync is in progress
  useEffect(() => {
    if (!syncStatus?.sync_progress?.in_progress) return;
    const interval = setInterval(() => {
      fetchSyncStatus();
    }, 5000);
    return () => clearInterval(interval);
  }, [syncStatus?.sync_progress?.in_progress]);

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API}/admin/transactions`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch transactions');
      
      const data = await response.json();
      setTransactions(data);
    } catch (error) {
      toast.error('Failed to load transactions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/shopify/status`, {
        headers: getAuthHeaders(),
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const handleResetSync = async () => {
    if (!confirm('Force-reset the stuck sync? This clears the progress doc so you can retrigger. (Does not stop a live backend task — only clears UI state.)')) {
      return;
    }
    try {
      const response = await fetch(`${API}/admin/shopify/sync/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Reset failed');
      }
      toast.success(result.message || 'Sync progress reset');
      setSyncing(false);
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to reset sync');
    }
  };

  const handleRangeSync = async () => {
    if (!rangeStart) {
      toast.error('Pick a start date');
      return;
    }
    if (rangeEnd && rangeStart > rangeEnd) {
      toast.error('End date must be on or after start date');
      return;
    }
    setSyncing(true);
    try {
      const params = new URLSearchParams({ start_date: rangeStart });
      if (rangeEnd) params.append('end_date', rangeEnd);
      const response = await fetch(`${API}/admin/shopify/sync?${params.toString()}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Sync failed');

      if (result.status === 'in_progress') {
        toast.info('A sync is already running. Monitoring progress...');
      } else {
        toast.success(result.message || 'Custom range sync started in background.');
      }
      setRangeDialogOpen(false);

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusResp = await fetch(`${API}/admin/shopify/status`, { headers: getAuthHeaders() });
          const statusData = await statusResp.json();
          setSyncStatus(statusData);
          const prog = statusData.sync_progress;
          if (prog && !prog.in_progress) {
            clearInterval(pollInterval);
            setSyncing(false);
            if (prog.status === 'completed') {
              toast.success(`Range sync complete! ${prog.processed_count} processed, ${prog.matched_count} matched`);
            } else if (prog.status === 'failed') {
              toast.error(`Sync failed: ${prog.error || 'Unknown error'}`);
            }
            fetchTransactions();
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 5000);
      setTimeout(() => clearInterval(pollInterval), 30 * 60 * 1000);
    } catch (error) {
      toast.error(error.message || 'Failed to start range sync');
      setSyncing(false);
    }
  };

  const handleShopifySync = async (fullResync = false) => {
    setSyncing(true);
    try {
      const url = fullResync 
        ? `${API}/admin/shopify/sync?full_resync=true`
        : `${API}/admin/shopify/sync`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Sync failed');
      }
      
      // Full resync runs in background - poll for completion
      if (fullResync && (result.status === 'started' || result.status === 'in_progress')) {
        if (result.status === 'in_progress') {
          toast.info('A full sync is already running. Monitoring progress...');
        } else {
          toast.success('Full 90-day sync started in background. This may take several minutes.');
        }
        // Poll every 5 seconds
        const pollInterval = setInterval(async () => {
          try {
            const statusResp = await fetch(`${API}/admin/shopify/status`, {
              headers: getAuthHeaders(),
            });
            const statusData = await statusResp.json();
            setSyncStatus(statusData);
            const prog = statusData.sync_progress;
            if (prog && !prog.in_progress) {
              clearInterval(pollInterval);
              setSyncing(false);
              if (prog.status === 'completed') {
                toast.success(`Full sync complete! ${prog.processed_count} processed, ${prog.matched_count} matched`);
              } else if (prog.status === 'failed') {
                toast.error(`Sync failed: ${prog.error || 'Unknown error'}`);
              }
              fetchTransactions();
            }
          } catch (err) {
            console.error('Poll error:', err);
          }
        }, 5000);
        // Safety: stop polling after 30 min
        setTimeout(() => clearInterval(pollInterval), 30 * 60 * 1000);
        return;
      }
      
      const draftInfo = result.draft_processed ? `, ${result.draft_processed} draft orders (${result.draft_matched} matched)` : '';
      toast.success(`Sync complete! ${result.processed} orders processed, ${result.matched} matched${draftInfo}`);
      fetchTransactions();
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to sync with Shopify');
    } finally {
      if (!fullResync) {
        setSyncing(false);
      }
    }
  };

  const handleDebugMatch = async () => {
    if (!debugPhone && !debugEmail && !debugOrderNumber) {
      toast.error('Please enter at least one search parameter');
      return;
    }
    
    setDebugLoading(true);
    setDebugResult(null);
    
    try {
      const params = new URLSearchParams();
      if (debugPhone) params.append('phone', debugPhone);
      if (debugEmail) params.append('email', debugEmail);
      if (debugOrderNumber) params.append('order_number', debugOrderNumber);
      
      const response = await fetch(`${API}/admin/debug/match-order?${params}`, {
        headers: getAuthHeaders(),
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Debug check failed');
      }
      
      setDebugResult(result);
    } catch (error) {
      toast.error(error.message || 'Failed to run debug check');
    } finally {
      setDebugLoading(false);
    }
  };

  const handleCheckShopifyOrder = async () => {
    if (!debugOrderNumber) {
      toast.error('Please enter an order number');
      return;
    }
    
    setCheckingShopify(true);
    setShopifyOrderResult(null);
    
    try {
      const response = await fetch(`${API}/admin/debug/shopify-order?order_number=${encodeURIComponent(debugOrderNumber)}`, {
        headers: getAuthHeaders(),
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to check Shopify');
      }
      
      setShopifyOrderResult(result);
    } catch (error) {
      toast.error(error.message || 'Failed to check Shopify order');
    } finally {
      setCheckingShopify(false);
    }
  };

  const handleForceSyncOrder = async () => {
    if (!debugOrderNumber) {
      toast.error('Please enter an order number');
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/debug/force-sync-order?order_number=${encodeURIComponent(debugOrderNumber)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to force sync order');
      }
      
      if (result.success) {
        toast.success(`Order ${result.order_name} synced! Result: ${result.result?.status || 'processed'}`);
        // Refresh the debug results
        handleCheckShopifyOrder();
        handleDebugMatch();
        fetchTransactions();
        fetchUnmatchedTransactions();
      } else {
        toast.error(result.error || 'Sync failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to force sync order');
    }
  };

  const handleConnectShopify = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API}/admin/shopify/oauth/url`, {
        headers: getAuthHeaders(),
        
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to get OAuth URL');
      }
      
      // Redirect to Shopify OAuth
      window.location.href = data.oauth_url;
    } catch (error) {
      toast.error(error.message || 'Failed to connect to Shopify');
      setConnecting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const transactions = jsonData.map(row => ({
        order_id: row['Order ID'] || row['order_id'] || '',
        customer_name: row['Customer Name'] || row['customer_name'] || '',
        customer_email: (row['Customer Email'] || row['customer_email'] || '').toLowerCase(),
        customer_phone: row['Customer Phone'] || row['customer_phone'] || '',
        referral_code: row['Referral Code'] || row['Discount Code'] || row['referral_code'] || '',
        amount: parseFloat(row['Amount'] || row['Total'] || row['amount'] || 0),
        transaction_date: row['Date'] || row['transaction_date'] || new Date().toISOString()
      }));

      const response = await fetch(`${API}/admin/shopify/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({ transactions })
      });

      if (!response.ok) throw new Error('Failed to import transactions');

      const result = await response.json();
      toast.success(result.message);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to upload file: ' + error.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      {
        'Order ID': 'ORD-001',
        'Customer Name': 'John Doe',
        'Customer Email': 'john@example.com',
        'Customer Phone': '+919876543210',
        'Referral Code': 'KB-JOH1234',
        'Amount': '5000',
        'Date': new Date().toISOString()
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, 'sample_orders.xlsx');
  };

  // Manual Match functions
  const fetchUnmatchedTransactions = async () => {
    try {
      const response = await fetch(`${API}/admin/transactions/unmatched`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setUnmatchedTransactions(data);
        setFilteredUnmatched(data);
      }
    } catch (error) {
      console.error('Failed to fetch unmatched transactions:', error);
    }
  };

  // Filter unmatched transactions by search query
  const handleUnmatchedSearch = (query) => {
    setUnmatchedSearch(query);
    if (!query.trim()) {
      setFilteredUnmatched(unmatchedTransactions);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const normalizedQuery = query.replace(/\D/g, ''); // For phone search
    
    const filtered = unmatchedTransactions.filter(txn => {
      const matchesName = txn.customer_name?.toLowerCase().includes(lowerQuery);
      const matchesEmail = txn.customer_email?.toLowerCase().includes(lowerQuery);
      const matchesOrder = (txn.order_number || txn.order_id || '').toLowerCase().includes(lowerQuery);
      const matchesPhone = txn.customer_phone && 
        txn.customer_phone.replace(/\D/g, '').includes(normalizedQuery);
      
      return matchesName || matchesEmail || matchesOrder || matchesPhone;
    });
    
    setFilteredUnmatched(filtered);
  };

  // Fetch partners once for the Attribute-to-Partner dropdown/search
  const fetchAllPartners = async () => {
    if (allPartners.length > 0) return;
    try {
      const resp = await fetch(`${API}/admin/partners/list`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setAllPartners(data);
      }
    } catch (err) {
      console.error('Failed to fetch partners list:', err);
    }
  };

  const openAttributeDialog = (txn, e) => {
    if (e) { e.stopPropagation(); }
    setAttributeTxn(txn);
    setPartnerSearch('');
    setAttributeDialogOpen(true);
    fetchAllPartners();
  };

  const handleAttributeToPartner = async (partnerId) => {
    if (!attributeTxn) return;
    setAttributing(true);
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/admin/transactions/attribute-to-partner`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = () => {
        setAttributing(false);
        let result;
        try { result = JSON.parse(xhr.responseText); } catch (e) { result = null; }
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(result?.message || 'Order attributed to partner');
          setAttributeDialogOpen(false);
          setAttributeTxn(null);
          fetchUnmatchedTransactions();
          fetchTransactions();
        } else if (xhr.status === 409 && result?.detail?.code === 'customer_already_attributed') {
          const d = result.detail;
          toast.error(
            `⚠️ Blocked: customer already a referral of "${d.existing_partner_name || d.existing_partner_id}". Merge duplicates first or choose a different action.`,
            { duration: 7000 }
          );
        } else {
          const msg = (result && (result.detail?.message || result.detail)) || `Failed to attribute (${xhr.status})`;
          toast.error(typeof msg === 'string' ? msg : 'Failed to attribute');
        }
      };
      xhr.onerror = () => { setAttributing(false); toast.error('Network error during attribution'); };
      xhr.send(JSON.stringify({ transaction_id: attributeTxn.transaction_id, partner_id: partnerId }));
    } catch (err) {
      setAttributing(false);
      toast.error('Attribution failed: ' + err.message);
    }
  };

  // Reconcile + Backfill ===================================================
  const handleReconcile = async () => {
    if (!reconcileStart || !reconcileEnd) return;
    setReconciling(true);
    setReconcileResult(null);
    try {
      // Use IST timezone offset (+05:30) so dates align with how users think about Apr 1, May 21 etc.
      const startISO = `${reconcileStart}T00:00:00+05:30`;
      const endISO = `${reconcileEnd}T23:59:59+05:30`;
      const url = `${API}/admin/shopify/reconcile?start_date=${encodeURIComponent(startISO)}&end_date=${encodeURIComponent(endISO)}`;
      const resp = await fetch(url, { headers: getAuthHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setReconcileResult(data);
      if (data.missing_count > 0) {
        toast.warning(`${data.missing_count} order(s) missing — click "Backfill" to fix.`);
      } else {
        toast.success('Database is in sync with Shopify for this range');
      }
    } catch (err) {
      toast.error(`Reconcile failed: ${err.message}`);
    } finally {
      setReconciling(false);
    }
  };

  const handleBackfillMissing = async () => {
    if (!reconcileStart || !reconcileEnd) return;
    setBackfilling(true);
    try {
      // Trigger the robust range sync over the same window — the new day-by-day fetcher
      // with count verification will pick up all missing orders.
      const startISO = `${reconcileStart}T00:00:00+05:30`;
      const endISO = `${reconcileEnd}T23:59:59+05:30`;
      const url = `${API}/admin/shopify/sync?start_date=${encodeURIComponent(startISO)}&end_date=${encodeURIComponent(endISO)}`;
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = async () => {
        setBackfilling(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success('Robust backfill started in background. Re-run Reconcile in 1–2 min to verify.');
          fetchSyncStatus();
        } else {
          toast.error(`Failed to start backfill (${xhr.status})`);
        }
      };
      xhr.onerror = () => { setBackfilling(false); toast.error('Network error'); };
      xhr.send();
    } catch (err) {
      setBackfilling(false);
      toast.error(`Backfill failed: ${err.message}`);
    }
  };

  const handleSearchReferrals = async (query) => {
    setReferralSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(`${API}/admin/referrals/search?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleManualMatch = async (referralId) => {
    if (!selectedTransaction) return;
    
    setMatching(true);
    try {
      const response = await fetch(`${API}/admin/transactions/manual-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          transaction_id: selectedTransaction.transaction_id,
          referral_id: referralId
        })
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to match transaction');
      }
      
      toast.success(`Matched to ${result.partner_name}. ₹${result.commission_amount.toFixed(2)} commission credited.`);
      setManualMatchDialogOpen(false);
      setSelectedTransaction(null);
      setReferralSearch('');
      setSearchResults([]);
      fetchTransactions();
      fetchUnmatchedTransactions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setMatching(false);
    }
  };

  const openManualMatchDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setManualMatchDialogOpen(true);
    setReferralSearch('');
    setSearchResults([]);
    fetchUnmatchedTransactions();
  };

  const handleDeleteTransaction = async (transactionId, orderNumber) => {
    if (!confirm(`Are you sure you want to delete transaction ${orderNumber}? This will allow you to re-sync and re-match the order.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/transactions/${transactionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete transaction');
      }
      
      toast.success(`Transaction ${orderNumber} deleted. You can now re-sync to re-match it.`);
      fetchTransactions();
      fetchUnmatchedTransactions();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalRevenue = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalCommission = transactions.reduce((sum, t) => sum + (t.commission_amount || 0), 0);
  const matchedCount = transactions.filter(t => t.partner_id).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Shopify Transactions
          </h1>
          <p className="text-muted-foreground">Sync orders from Shopify or import manually</p>
        </div>
      </div>

      {/* Shopify Sync Card */}
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-primary" />
            Shopify Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {syncStatus?.configured ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">Connected to {syncStatus.store_url}</span>
                  </>
                ) : syncStatus?.oauth_ready ? (
                  <>
                    <Link2 className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-600">Ready to connect - Click "Connect Shopify" to authorize</span>
                  </>
                ) : (
                  <>
                    <CloudOff className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium text-red-600">Not configured</span>
                  </>
                )}
              </div>
              {syncStatus?.configured && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Auto-sync: {syncStatus.sync_interval}
                  </span>
                  {syncStatus.last_sync && (
                    <span>
                      Last sync: {new Date(syncStatus.last_sync).toLocaleString()} 
                      ({syncStatus.last_processed} processed, {syncStatus.last_matched} matched)
                    </span>
                  )}
                </div>
              )}
              {syncStatus?.sync_progress?.in_progress && (
                <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="full-sync-progress-banner">
                  <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {syncStatus.sync_progress.range_start ? (
                      <>Custom range sync in progress ({syncStatus.sync_progress.range_start.slice(0,10)} → {syncStatus.sync_progress.range_end ? syncStatus.sync_progress.range_end.slice(0,10) : 'now'})...</>
                    ) : (
                      <>Full 90-day sync in progress...</>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-blue-700">
                    {syncStatus.sync_progress.status === 'fetching_orders' && (
                      <>Fetching orders from Shopify (this can take several minutes for large stores)...</>
                    )}
                    {syncStatus.sync_progress.status === 'processing_orders' && (
                      <>
                        Examined {syncStatus.sync_progress.processed_count || 0} / {syncStatus.sync_progress.total_orders || 0} orders
                        {syncStatus.sync_progress.matched_count ? ` (${syncStatus.sync_progress.matched_count} matched)` : ''}
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleResetSync}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                    data-testid="cancel-sync-button"
                  >
                    Cancel / Reset stuck sync
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Orders with discount codes starting with "KB-" are automatically matched to partners
              </p>
              {syncStatus?.configured && (
                <p className="text-xs text-blue-600 mt-1">
                  💡 Click "Reconnect" to update Shopify permissions (required for automatic discount code creation)
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {!syncStatus?.configured && syncStatus?.oauth_ready && (
                <Button 
                  onClick={handleConnectShopify} 
                  disabled={connecting}
                  className="min-w-[160px] bg-green-600 hover:bg-green-700"
                  data-testid="connect-shopify-button"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Connect Shopify
                    </>
                  )}
                </Button>
              )}
              {syncStatus?.configured && (
                <>
                  <Button 
                    onClick={handleConnectShopify} 
                    disabled={connecting}
                    variant="outline"
                    className="min-w-[140px]"
                    data-testid="reconnect-shopify-button"
                    title="Reconnect to update permissions or fix connection issues"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Reconnecting...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Reconnect
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => handleShopifySync(false)} 
                    disabled={syncing}
                    className="min-w-[140px]"
                    data-testid="sync-shopify-button"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sync Now
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => handleShopifySync(true)} 
                    disabled={syncing || syncStatus?.sync_progress?.in_progress}
                    variant="outline"
                    className="min-w-[140px]"
                    data-testid="full-resync-button"
                    title="Fetches orders from the last 90 days to rematch older orders"
                  >
                    {(syncing || syncStatus?.sync_progress?.in_progress) ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Full Resync (90 days)
                      </>
                    )}
                  </Button>
                  
                  {/* Custom Date Range Sync */}
                  <Dialog open={rangeDialogOpen} onOpenChange={setRangeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="min-w-[140px]"
                        disabled={syncing || syncStatus?.sync_progress?.in_progress}
                        data-testid="custom-range-button"
                        title="Sync orders from a specific date range"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Custom Range
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md" data-testid="custom-range-dialog">
                      <DialogHeader>
                        <DialogTitle>Sync Custom Date Range</DialogTitle>
                        <DialogDescription>
                          Pick a start (and optional end) date to fetch and re-process orders within that window. Useful for catching specific historical orders that didn't get matched.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        <div>
                          <Label htmlFor="range-start">Start date *</Label>
                          <Input
                            id="range-start"
                            type="date"
                            value={rangeStart}
                            onChange={(e) => setRangeStart(e.target.value)}
                            max={rangeEnd || new Date().toISOString().slice(0, 10)}
                            data-testid="range-start-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="range-end">End date (optional)</Label>
                          <Input
                            id="range-end"
                            type="date"
                            value={rangeEnd}
                            onChange={(e) => setRangeEnd(e.target.value)}
                            min={rangeStart}
                            max={new Date().toISOString().slice(0, 10)}
                            data-testid="range-end-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Leave blank to sync from start date until today.
                          </p>
                        </div>
                        {rangeStart && rangeEnd && rangeStart > rangeEnd && (
                          <p className="text-sm text-red-600">End date must be on or after start date.</p>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setRangeDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRangeSync}
                          disabled={!rangeStart || (rangeEnd && rangeStart > rangeEnd) || syncing}
                          data-testid="range-sync-confirm-button"
                        >
                          Start Sync
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* Reconcile Range (verify + backfill) */}
                  <Dialog open={reconcileDialogOpen} onOpenChange={setReconcileDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="min-w-[140px]"
                        data-testid="reconcile-button"
                        title="Verify which orders are missing from a date range, then bulk-backfill"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Reconcile
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-dialog">
                      <DialogHeader>
                        <DialogTitle>Reconcile Shopify ↔ Database</DialogTitle>
                        <DialogDescription>
                          Asks Shopify for the authoritative order count in a date range and compares it against our database. If orders are missing, you can backfill them in one click.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="reconcile-start">Start date *</Label>
                            <Input id="reconcile-start" type="date" value={reconcileStart} onChange={(e) => setReconcileStart(e.target.value)} max={reconcileEnd || new Date().toISOString().slice(0, 10)} data-testid="reconcile-start-input" />
                          </div>
                          <div>
                            <Label htmlFor="reconcile-end">End date *</Label>
                            <Input id="reconcile-end" type="date" value={reconcileEnd} onChange={(e) => setReconcileEnd(e.target.value)} min={reconcileStart} max={new Date().toISOString().slice(0, 10)} data-testid="reconcile-end-input" />
                          </div>
                        </div>
                        <Button onClick={handleReconcile} disabled={!reconcileStart || !reconcileEnd || reconciling} data-testid="reconcile-run-button">
                          {reconciling ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking…</> : 'Check for missing orders'}
                        </Button>

                        {reconcileResult && (
                          <div className="space-y-3 mt-3">
                            <div className="grid grid-cols-3 gap-3">
                              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                                <div className="text-xs text-muted-foreground">Shopify (paid/fulfilled)</div>
                                <div className="text-2xl font-semibold">{reconcileResult.shopify_paid_or_fulfilled_in_range}</div>
                              </div>
                              <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                                <div className="text-xs text-muted-foreground">Database</div>
                                <div className="text-2xl font-semibold">{reconcileResult.db_count_in_range}</div>
                              </div>
                              <div className={`p-3 rounded-lg border ${reconcileResult.missing_count > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                                <div className="text-xs text-muted-foreground">Missing</div>
                                <div className={`text-2xl font-semibold ${reconcileResult.missing_count > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="reconcile-missing-count">{reconcileResult.missing_count}</div>
                              </div>
                            </div>

                            {reconcileResult.missing_count > 0 && (
                              <div>
                                <p className="text-sm text-muted-foreground mb-2">First {Math.min(reconcileResult.missing_order_numbers.length, 50)} missing: <code className="text-xs">{reconcileResult.missing_order_numbers.join(', ')}</code></p>
                                <Button
                                  onClick={handleBackfillMissing}
                                  disabled={backfilling}
                                  className="bg-orange-500 hover:bg-orange-600 text-white"
                                  data-testid="reconcile-backfill-button"
                                >
                                  {backfilling ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Backfilling {reconcileResult.missing_count}…</> : `Backfill ${reconcileResult.missing_count} missing orders`}
                                </Button>
                                {reconcileResult.missing_count > 50 && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    💡 Only the first 50 are listed above, but <strong>all {reconcileResult.missing_count}</strong> will be backfilled. The robust day-by-day fetcher will refetch the entire range and verify each day's count against Shopify.
                                  </p>
                                )}
                              </div>
                            )}
                            {reconcileResult.missing_count === 0 && (
                              <p className="text-sm text-emerald-700">✅ Your database is in sync with Shopify for this range.</p>
                            )}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Debug Match Button */}
                  <Dialog open={debugDialogOpen} onOpenChange={setDebugDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline"
                        className="min-w-[140px]"
                        data-testid="debug-match-button"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Debug Match
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Debug Order Matching</DialogTitle>
                        <DialogDescription>
                          Enter order details to diagnose why an order isn't matching to a referral.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="debug-phone">Customer Phone</Label>
                            <Input
                              id="debug-phone"
                              value={debugPhone}
                              onChange={(e) => setDebugPhone(e.target.value)}
                              placeholder="9820310353"
                            />
                          </div>
                          <div>
                            <Label htmlFor="debug-email">Customer Email</Label>
                            <Input
                              id="debug-email"
                              value={debugEmail}
                              onChange={(e) => setDebugEmail(e.target.value)}
                              placeholder="customer@email.com"
                            />
                          </div>
                          <div>
                            <Label htmlFor="debug-order">Order Number</Label>
                            <Input
                              id="debug-order"
                              value={debugOrderNumber}
                              onChange={(e) => setDebugOrderNumber(e.target.value)}
                              placeholder="KBT11355"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleDebugMatch} disabled={debugLoading}>
                            {debugLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Checking...
                              </>
                            ) : (
                              <>
                                <Search className="h-4 w-4 mr-2" />
                                Check Database
                              </>
                            )}
                          </Button>
                          <Button onClick={handleCheckShopifyOrder} disabled={checkingShopify} variant="outline">
                            {checkingShopify ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Checking Shopify...
                              </>
                            ) : (
                              <>
                                <Cloud className="h-4 w-4 mr-2" />
                                Check Shopify Directly
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {/* Shopify Order Result */}
                        {shopifyOrderResult && (
                          <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                            <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-700 dark:text-blue-300">
                              <Cloud className="h-4 w-4" />
                              Shopify Order Status
                            </h4>
                            {shopifyOrderResult.found ? (
                              <div className="text-sm space-y-2">
                                {/* Sync Eligibility Banner */}
                                <div className={`p-2 rounded ${shopifyOrderResult.will_sync ? 'bg-green-100 dark:bg-green-950' : 'bg-red-100 dark:bg-red-950'}`}>
                                  <p className={`font-semibold ${shopifyOrderResult.will_sync ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                                    {shopifyOrderResult.will_sync ? '✅ This order WILL be synced' : '❌ This order will NOT be synced'}
                                    {shopifyOrderResult.is_cod && ' (COD Order)'}
                                  </p>
                                  {shopifyOrderResult.sync_reason?.map((reason, i) => (
                                    <p key={i} className="text-sm mt-1">{reason}</p>
                                  ))}
                                </div>
                                
                                <p><strong>Order:</strong> {shopifyOrderResult.order.name} (ID: {shopifyOrderResult.order.id})</p>
                                <p><strong>Created:</strong> {shopifyOrderResult.order.created_at}</p>
                                <p><strong>Payment Status:</strong> {shopifyOrderResult.order.financial_status || 'N/A'}</p>
                                <p><strong>Fulfillment:</strong> {shopifyOrderResult.order.fulfillment_status || 'unfulfilled'}</p>
                                <p><strong>Amount:</strong> ₹{shopifyOrderResult.order.total_price}</p>
                                <p><strong>Customer Email:</strong> {shopifyOrderResult.order.customer_email || 'N/A'}</p>
                                <p><strong>Customer Phone:</strong> {shopifyOrderResult.order.customer_phone || 'N/A'}</p>
                                <p><strong>Customer Name:</strong> {shopifyOrderResult.order.customer_name || 'N/A'}</p>
                                <p><strong>Discount Codes:</strong> {shopifyOrderResult.order.discount_codes?.join(', ') || 'None'}</p>
                                {shopifyOrderResult.order.cancelled_at && (
                                  <p className="text-red-500"><strong>Cancelled:</strong> {shopifyOrderResult.order.cancelled_at}</p>
                                )}
                                
                                {/* Force Sync Button */}
                                {shopifyOrderResult.will_sync && debugResult?.existing_transaction === null && (
                                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                                      This order exists in Shopify but not in your database. Click below to sync it now.
                                    </p>
                                    <Button 
                                      onClick={handleForceSyncOrder}
                                      size="sm"
                                      data-testid="force-sync-order-btn"
                                    >
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                      Force Sync This Order
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-red-500">❌ {shopifyOrderResult.message || 'Order not found in Shopify'}</p>
                            )}
                            {shopifyOrderResult.error && (
                              <p className="text-red-500">Error: {shopifyOrderResult.error}</p>
                            )}
                          </div>
                        )}
                        
                        {debugResult && (
                          <div className="space-y-4 mt-4 p-4 bg-muted rounded-lg">
                            {/* Diagnosis Summary */}
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                Database Diagnosis
                              </h4>
                              <div className="space-y-1">
                                {debugResult.diagnosis?.map((d, i) => (
                                  <div key={i} className={`text-sm p-2 rounded flex items-center gap-2 ${
                                    d.includes('NOT FOUND') || d.includes('NO MATCHING') 
                                      ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' 
                                      : d.includes('EXISTS') || d.includes('Found')
                                      ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
                                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300'
                                  }`}>
                                    {d.includes('NOT FOUND') || d.includes('NO MATCHING') ? (
                                      <XCircle className="h-4 w-4 flex-shrink-0" />
                                    ) : d.includes('EXISTS') || d.includes('Found') ? (
                                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                                    ) : (
                                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    )}
                                    {d}
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Search Parameters */}
                            <div>
                              <h4 className="font-semibold mb-2">Search Parameters</h4>
                              <div className="text-sm bg-background p-2 rounded">
                                <p><strong>Phone:</strong> {debugResult.search_params?.phone || 'N/A'} 
                                  {debugResult.search_params?.phone_normalized && (
                                    <span className="text-muted-foreground"> (normalized: {debugResult.search_params.phone_normalized})</span>
                                  )}
                                </p>
                                <p><strong>Email:</strong> {debugResult.search_params?.email || 'N/A'}</p>
                                <p><strong>Order #:</strong> {debugResult.search_params?.order_number || 'N/A'}</p>
                              </div>
                            </div>
                            
                            {/* Existing Transaction */}
                            <div>
                              <h4 className="font-semibold mb-2">Transaction in Database</h4>
                              {debugResult.existing_transaction ? (
                                <div className="text-sm bg-background p-2 rounded space-y-1">
                                  <p><strong>Order:</strong> {debugResult.existing_transaction.order_number}</p>
                                  <p><strong>Customer:</strong> {debugResult.existing_transaction.customer_email} / {debugResult.existing_transaction.customer_phone}</p>
                                  <p><strong>Amount:</strong> ₹{debugResult.existing_transaction.amount}</p>
                                  <p><strong>Partner ID:</strong> {debugResult.existing_transaction.partner_id || <span className="text-red-500">NOT MATCHED</span>}</p>
                                  <p><strong>Matched Via:</strong> {debugResult.existing_transaction.matched_via || 'N/A'}</p>
                                </div>
                              ) : (
                                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950 p-2 rounded">
                                  ❌ Transaction not found in database - order may not have been synced from Shopify
                                </p>
                              )}
                            </div>
                            
                            {/* Matching Referrals */}
                            <div>
                              <h4 className="font-semibold mb-2">Matching Referrals Found</h4>
                              {debugResult.matching_referrals?.length > 0 ? (
                                <div className="space-y-2">
                                  {debugResult.matching_referrals.map((ref, i) => (
                                    <div key={i} className="text-sm bg-green-50 dark:bg-green-950 p-2 rounded">
                                      <p><strong>Match Type:</strong> {ref.match_type}</p>
                                      <p><strong>Customer:</strong> {ref.customer_name}</p>
                                      <p><strong>Phone:</strong> {ref.customer_phone} {ref.customer_phone_normalized && `(${ref.customer_phone_normalized})`}</p>
                                      <p><strong>Email:</strong> {ref.customer_email}</p>
                                      <p><strong>Partner ID:</strong> {ref.partner_id}</p>
                                      <p><strong>Created:</strong> {ref.created_at}</p>
                                      <p><strong>Status:</strong> {ref.status}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950 p-2 rounded">
                                  ❌ No matching referrals found with the given phone/email
                                </p>
                              )}
                            </div>
                            
                            {/* Sample Referrals */}
                            {debugResult.all_referrals_sample?.length > 0 && (
                              <div>
                                <h4 className="font-semibold mb-2 text-muted-foreground">Sample Referrals in Database (for comparison)</h4>
                                <div className="text-xs bg-background p-2 rounded space-y-1 max-h-32 overflow-y-auto">
                                  {debugResult.all_referrals_sample.map((ref, i) => (
                                    <p key={i}>
                                      {ref.customer_name} - Phone: {ref.customer_phone || 'N/A'} ({ref.phone_normalized || 'N/A'})
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Manual Import
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={downloadSampleCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download Sample
            </Button>
            <Button 
              onClick={() => document.getElementById('file-upload').click()} 
              disabled={uploading}
              variant="outline"
              size="sm"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Excel/CSV'}
            </Button>
            <Input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-upload-input"
            />
            <Button 
              onClick={() => window.open(`${API}/admin/transactions/export`, '_blank')} 
              variant="outline"
              size="sm"
              data-testid="export-transactions-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Include columns: Order ID, Customer Name, Customer Email, Customer Phone, Referral Code, Amount, Date
          </p>
        </CardContent>
      </Card>

      {/* Manual Match Card - Only show if there are unmatched transactions */}
      {unmatchedTransactions.length > 0 && (
        <Card className="border-2 border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-orange-500" />
              Manual Match ({unmatchedTransactions.length} Unmatched)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              These transactions could not be automatically matched. Search or scroll to find an order, then click to manually link it to a referral.
            </p>
            
            {/* Search Input for Unmatched Transactions */}
            <div className="mb-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Search by order #, name, email, or phone..."
                  value={unmatchedSearch}
                  onChange={(e) => handleUnmatchedSearch(e.target.value)}
                  className="flex-1"
                  data-testid="unmatched-search-input"
                />
                {unmatchedSearch && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleUnmatchedSearch('')}
                  >
                    Clear
                  </Button>
                )}
              </div>
              {unmatchedSearch && (
                <p className="text-xs text-muted-foreground mt-1">
                  Showing {filteredUnmatched.length} of {unmatchedTransactions.length} transactions
                </p>
              )}
            </div>
            
            {/* Scrollable list of ALL unmatched transactions */}
            <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-2 bg-background">
              {filteredUnmatched.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {unmatchedSearch ? `No transactions match "${unmatchedSearch}"` : 'No unmatched transactions'}
                </p>
              ) : (
                filteredUnmatched.map((txn) => (
                  <div 
                    key={txn.transaction_id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors"
                    data-testid={`unmatched-txn-${txn.transaction_id}`}
                  >
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openManualMatchDialog(txn)}>
                      <p className="font-medium">{txn.order_number || txn.order_id}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {txn.customer_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {txn.customer_email || ''} {txn.customer_phone ? `• ${txn.customer_phone}` : ''}
                      </p>
                    </div>
                    <div className="text-right ml-4 flex items-center gap-3">
                      <div>
                        <p className="font-medium">₹{txn.amount?.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(txn.transaction_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => openAttributeDialog(txn, e)}
                        data-testid={`attribute-btn-${txn.transaction_id}`}
                      >
                        Attribute
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Match Dialog */}
      <Dialog open={manualMatchDialogOpen} onOpenChange={setManualMatchDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Manual Match Transaction
            </DialogTitle>
            <DialogDescription>
              Search for a referral to link this transaction to
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              {/* Transaction Info */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-semibold mb-2">Transaction Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Order:</strong> {selectedTransaction.order_number || selectedTransaction.order_id}</p>
                  <p><strong>Amount:</strong> ₹{selectedTransaction.amount?.toLocaleString('en-IN')}</p>
                  <p><strong>Customer:</strong> {selectedTransaction.customer_name}</p>
                  <p><strong>Date:</strong> {new Date(selectedTransaction.transaction_date).toLocaleDateString()}</p>
                  <p><strong>Email:</strong> {selectedTransaction.customer_email || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedTransaction.customer_phone || 'N/A'}</p>
                </div>
              </div>
              
              {/* Search Referrals */}
              <div>
                <Label className="text-sm font-medium">Search Referrals</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Search by name, email, or phone..."
                    value={referralSearch}
                    onChange={(e) => handleSearchReferrals(e.target.value)}
                    data-testid="referral-search-input"
                  />
                  {searching && <Loader2 className="h-5 w-5 animate-spin self-center" />}
                </div>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Select a Referral to Match</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {searchResults.map((ref) => (
                      <div 
                        key={ref.referral_id}
                        className="flex items-center justify-between p-3 bg-background rounded-lg border cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                        onClick={() => handleManualMatch(ref.referral_id)}
                        data-testid={`referral-option-${ref.referral_id}`}
                      >
                        <div>
                          <p className="font-medium">{ref.customer_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {ref.customer_email} • {ref.customer_phone || 'No phone'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Partner: {ref.partner_name} • Status: {ref.status}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          disabled={matching}
                          variant="outline"
                        >
                          {matching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Match'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {referralSearch.length >= 2 && searchResults.length === 0 && !searching && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No referrals found matching "{referralSearch}"
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attribute to Partner Dialog */}
      <Dialog open={attributeDialogOpen} onOpenChange={(o) => { setAttributeDialogOpen(o); if (!o) setAttributeTxn(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Attribute Order to Partner
            </DialogTitle>
            <DialogDescription>
              Creates a new referral for this customer under the chosen partner and credits commission at their current tier rate. Blocked if the customer already belongs to another partner.
            </DialogDescription>
          </DialogHeader>

          {attributeTxn && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{attributeTxn.order_number || attributeTxn.order_id}</p>
                    <p className="text-sm">{attributeTxn.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {attributeTxn.customer_email || 'No email'} {attributeTxn.customer_phone ? `• ${attributeTxn.customer_phone}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">₹{attributeTxn.amount?.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-muted-foreground">{new Date(attributeTxn.transaction_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Choose a partner</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or referral code…"
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    className="pl-9"
                    data-testid="attribute-partner-search"
                  />
                </div>
              </div>

              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                {(() => {
                  const q = partnerSearch.trim().toLowerCase();
                  const list = q
                    ? allPartners.filter((p) =>
                        (p.name || '').toLowerCase().includes(q)
                        || (p.email || '').toLowerCase().includes(q)
                        || (p.referral_code || '').toLowerCase().includes(q)
                      )
                    : allPartners;
                  if (allPartners.length === 0) {
                    return <p className="text-sm text-muted-foreground text-center py-4">Loading partners…</p>;
                  }
                  if (list.length === 0) {
                    return <p className="text-sm text-muted-foreground text-center py-4">No partners match "{partnerSearch}"</p>;
                  }
                  return list.slice(0, 50).map((p) => (
                    <div
                      key={p.user_id}
                      className="flex items-center justify-between p-3 hover:bg-primary/5"
                      data-testid={`attribute-partner-option-${p.user_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {p.email}{p.referral_code ? ` • ${p.referral_code}` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        disabled={attributing}
                        onClick={() => handleAttributeToPartner(p.user_id)}
                        className="bg-orange-500 hover:bg-orange-600 text-white ml-2"
                        data-testid={`attribute-confirm-${p.user_id}`}
                      >
                        {attributing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Attribute'}
                      </Button>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {transactions.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ₹{totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Matched Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {matchedCount}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {transactions.length > 0 ? ((matchedCount / transactions.length) * 100).toFixed(1) : 0}% match rate
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Commission</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ₹{totalCommission.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Transactions ({transactions.length})</CardTitle>
            <Button onClick={fetchTransactions} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Cloud className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No transactions yet</p>
              <p className="text-sm">Sync with Shopify or upload orders manually to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Order</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Customer</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Referral Code</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Partner</th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">Revenue</th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">Commission</th>
                    <th className="text-center p-3 text-sm font-medium text-muted-foreground">Source</th>
                    <th className="text-center p-3 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-center p-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.transaction_id} data-testid={`transaction-${txn.transaction_id}`} className="border-b border-border hover:bg-accent/50">
                      <td className="p-3">
                        <span className="font-mono text-sm">{txn.order_number || txn.order_id}</span>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{txn.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{txn.customer_email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        {txn.referral_code ? (
                          <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                            {txn.referral_code}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        {txn.partner_id ? (
                          <div>
                            <Badge variant="secondary">{txn.partner_name}</Badge>
                            {txn.matched_via && (
                              <p className="text-xs text-muted-foreground mt-1">via {txn.matched_via}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Unmatched</span>
                        )}
                      </td>
                      <td className="p-3 text-right font-medium">₹{txn.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="p-3 text-right font-semibold text-primary">
                        {txn.commission_amount > 0 ? `₹${txn.commission_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={txn.source === 'shopify_api' ? 'default' : 'outline'} className="text-xs">
                          {txn.source === 'shopify_api' ? 'API' : 'Import'}
                        </Badge>
                      </td>
                      <td className="p-3 text-center text-sm text-muted-foreground">
                        {new Date(txn.transaction_date).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteTransaction(txn.transaction_id, txn.order_number || txn.order_id)}
                          data-testid={`delete-txn-${txn.transaction_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
